import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Auth } from '@aws-amplify/auth';
import { JsonViewer } from '@dataxpdtn/mui-json-viewer';
import config from '@iotv/config';
import { existsFilter, getRejectionBucketingFilter } from '@iotv/core-services';
import { DeviceControlTransaction, getNiceDate, getNiceTimeOnly, getS3TimeKeyPrefix, isDeviceControlTranasaction, isValidDeviceObject, Milliseconds, S3TimeKeyLevel, sortByTimestamp, } from '@iotv/datamodel-core';
import { SortOrder } from '@iotv/iotv-v3-types';
import { LinkType } from '@iotv/lns';
import { s3KeyRangePaginator } from '@iotv/s3';
import { Alert, AlertTitle, Button, Stack, ThemeProvider } from '@mui/material';
import dayjs from 'dayjs';
import { serviceDefinition } from 'internal-config';
import { useContext, useEffect, useState } from 'react';
import { denseDataGridTheme } from '../../../../cosmetics/Themes/DenseDataGrid';
import { getObjectFromS3 } from '../../../../data/aws/s3/api/s3';
import { ConsumerDao } from '../../../../data/ConsumerDAO';
import { PeriodRecent, useModularTimePeriodSelector } from '../../../../hooks/useModularTimePeriodSelector';
import { DialogWrapper } from '../../../common/DiaglogWrapper';
import { DecodedPayloadPopUp, StripedDataGrid } from '../../../factories/DataGrid';
import { DeviceConfigContext } from '../../Context/deviceConfigContext';
import { dlHistoryColumns } from './colDef';
const debug = process.env.REACT_APP_DEBUG && false;
const dyn = config.aws.dynamoDB;
export const DownlinkHistory = () => {
    const { state: deviceContextState, dispatch } = useContext(DeviceConfigContext);
    const { device, downlinkHistory } = deviceContextState;
    const [refreshTimestamp, setRefreshTimestamp] = useState();
    const lastRefreshMessage = refreshTimestamp ? `Refreshed at ${getNiceTimeOnly(refreshTimestamp)}` : 'Refreshing now...';
    const noDownlinkHistoryMessage = `No downlinks for ${device?.name ?? device?.eui ?? ''}`;
    const { components: { TimePeriodSelector }, fromDateValue, toDateValue, } = useModularTimePeriodSelector({
        rangeUnit: 'days',
        label: 'System Self Check (dates)',
        initialPeriodRecent: PeriodRecent.RECENT,
        rangeLength: 1,
        requireConfirmation: true,
    });
    const getDatabaseDCTs = async () => {
        if (!device)
            return;
        const { err, data } = await ConsumerDao.baseQuery({
            TableName: dyn.tableName,
            IndexName: dyn.tsiName,
            ExpressionAttributeNames: {
                '#pk': dyn.pKey,
                '#t': dyn.tsiSortKey,
                '#sk': dyn.sKey,
            },
            ExpressionAttributeValues: {
                ':pk': device.sk,
                ':t': Date.now() - 1.1 * Milliseconds.DAY,
                ':sk': 'DeviceControlTransaction',
            },
            KeyConditionExpression: '#pk = :pk and #t > :t',
            FilterExpression: 'begins_with(#sk, :sk)',
            ScanIndexForward: false,
        });
        if (err)
            console.log(`getDatabaseDCTs returned err: ${err.message}`);
        if (data?.Items) {
            const databaseDCTs = data.Items.filter(isDeviceControlTranasaction).map((dct) => new DeviceControlTransaction({ ...dct, devicePk: device.pk }));
            if (0 < databaseDCTs.length) {
                const dcts = { ...structuredClone(downlinkHistory), ...Object.fromEntries(structuredClone(databaseDCTs).map((dct) => [dct.sk, dct])) };
                debug && console.log(`getDatabaseDCTs dispatching ${databaseDCTs.length} dcts`);
                dispatch({ downlinkHistory: dcts });
                setRefreshTimestamp(Date.now());
            }
        }
    };
    // when Dates change and keys are present filter for display, having first retrieved any null entry values
    const filterKeys = async () => {
        if (!device)
            return;
        const credentials = await Auth.currentCredentials();
        debug && console.log('Content Records : in filterKeys');
        const filteredKeys = [];
        try {
            const StartTimeMs = fromDateValue.unix() * Milliseconds.SECOND;
            const endOfToDatePeriod = toDateValue.add(1, 'month').endOf('hour');
            const EndTimeMs = endOfToDatePeriod.unix() * Milliseconds.SECOND;
            const DurationMs = EndTimeMs - StartTimeMs;
            const keyFilter = (k) => k.endsWith(`Device/${device.eui}`);
            const rangeKeyGenerator = s3KeyRangePaginator({
                credentials,
                Bucket: serviceDefinition.S3.device_control_downlinks.name,
                keyFilter,
                StartTimeMs,
                DurationMs,
                level: S3TimeKeyLevel.MONTH,
                s3Folder: undefined,
            });
            for await (const contentRecords of rangeKeyGenerator) {
                console.log('contentRecords ', contentRecords);
                if (contentRecords) {
                    filteredKeys.push(...contentRecords);
                }
                else {
                    console.log('No content records to push ');
                }
            }
            const recordsToFetch = [];
            const matcher = (contentRecord) => {
                const exists = !!Object.values(downlinkHistory ?? {}).find((dct) => {
                    const inferredKey = `${getS3TimeKeyPrefix({ timestamp: dct.timestamp, s3TimeKeyLevel: S3TimeKeyLevel.MONTH })}/Device/${dct.initialRequest.device.eui}`;
                    debug && console.log('InferredKey');
                    return inferredKey === contentRecord.Key;
                });
                return exists;
            };
            const rejectionFilter = getRejectionBucketingFilter({ filter: matcher, rejectionsBucket: recordsToFetch });
            const existingRecords = filteredKeys.filter(rejectionFilter);
            debug && console.log('Content Records', { existingRecords, recordsToFetch });
            if (recordsToFetch.length > 0) {
                const retrievedDCTs = (await Promise.all(recordsToFetch.map(async (record) => {
                    const { err, data } = await getObjectFromS3({
                        Key: record.Key,
                        bucketIdentifier: serviceDefinition.S3.device_control_downlinks.name,
                    });
                    if (err) {
                        console.log(`Err retreiving ${record.Key}: ${err.message}`);
                    }
                    if (data) {
                        debug && console.log(`Fetched ${record.Key}`);
                        return data.filter(isDeviceControlTranasaction);
                    }
                    return null;
                })))
                    .flat()
                    .filter(existsFilter)
                    .filter(isDeviceControlTranasaction)
                    .map((dct) => new DeviceControlTransaction({ ...dct, devicePk: device.pk }));
                if (0 < retrievedDCTs.length) {
                    const dcts = { ...structuredClone(downlinkHistory), ...Object.fromEntries(structuredClone(retrievedDCTs).map((dct) => [dct.sk, dct])) };
                    dispatch({ downlinkHistory: dcts });
                }
            }
        }
        catch (e) {
            console.log('Error getting keys', e.message);
        }
    };
    useEffect(() => {
        getDatabaseDCTs();
        filterKeys();
    }, [device?.eui, fromDateValue, toDateValue]);
    const [wRef, setWRef] = useState(null);
    const getPayloadDialog = (children, header) => (_jsx(DialogWrapper, { header,
        open: true,
        setClosed: () => setWRef(null),
        children,
        fullWidth: true,
        maxWidth: 'xl' }));
    const handleCellClick = (e) => {
        const matchedKey = 'sentPayloadStr';
        if (isValidDeviceObject(device)) {
            if (e.field === matchedKey) {
                const payloadStr = e.value;
                if (typeof payloadStr === 'string') {
                    const linkType = LinkType.DOWN;
                    const port = null;
                    setWRef(getPayloadDialog(_jsx(DecodedPayloadPopUp, { payloadStr, linkType, device, port }), 'Decoded payload'));
                }
            }
            debug && console.log(e);
        }
    };
    const rows = Object.values(downlinkHistory ?? {})
        .filter((dct) => {
        const dTime = dayjs(dct.timestamp);
        return fromDateValue <= dTime && dTime < toDateValue;
    })
        .sort((a, b) => sortByTimestamp(a, b, SortOrder.DESCENDING));
    const dataGridProps = {
        onCellClick: handleCellClick,
        density: 'compact',
        columns: dlHistoryColumns,
        rows,
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        rowHeight: 32,
        columnHeaderHeight: 32,
        rowSelection: false,
        disableColumnMenu: true,
        disableColumnSelector: true,
        disableColumnResize: true,
        disableVirtualization: true,
        initialState: { sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] } },
    };
    return (_jsxs(Stack, { marginBlockStart: 1, gap: 1, children: [_jsxs(Alert, { severity: "info", action: _jsx(Button, { color: "inherit", size: "small", onClick: () => {
                        getDatabaseDCTs();
                        filterKeys();
                    }, children: "Refresh" }), children: [Object.keys(downlinkHistory ?? {}).length === 0 && _jsx(AlertTitle, { children: noDownlinkHistoryMessage }), lastRefreshMessage] }), TimePeriodSelector, isValidDeviceObject(device) && 0 < Object.keys(downlinkHistory ?? {}).length && (_jsx(ThemeProvider, { theme: denseDataGridTheme, children: _jsx(StripedDataGrid, { ...dataGridProps }) })), debug && downlinkHistory && 0 < Object.keys(downlinkHistory).length && (_jsx(JsonViewer, { value: Object.values(downlinkHistory)
                    .sort((a, b) => sortByTimestamp(a, b, SortOrder.DESCENDING))
                    .map((dct) => getNiceDate(dct.timestamp, { year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })) })), wRef] }));
};
